/**
 * THE DELTA CHIP — ported unchanged from analytics-shared.tsx, because all four
 * of its rules are ways this screen could state a falsehood confidently.
 *
 *  · A `live` figure gets NO CHIP. It is counted as of now rather than as of the
 *    end of the window, so both windows would return the same number and the
 *    delta would be a guaranteed zero presented as evidence of stability. The
 *    server already sends `delta: null`; this refuses to render one even if that
 *    ever changed.
 *  · `delta: null` with no comparison window asked for gets no chip either —
 *    silence, not a zero.
 *  · `likeForLike: false` suppresses the PERCENTAGE and prints the two spans. A
 *    percent change between a 12-day window and a 30-day one is arithmetic
 *    without a meaning.
 *  · A percentage against a previous value of ZERO is not a percentage. It is
 *    shown as an absolute change only, because "+300%" from a base of nothing
 *    reads as a trend and is a rounding artefact of the first record ever
 *    logged.
 *
 * THE CHIP IS VERDICT-FREE. It carries no success/alert tone, deliberately: more
 * visitors is not good news, it is Tuesday, and painting a busy week green
 * invents a judgement the data does not carry. Only the glyph and the sign say
 * which way it went.
 */
import React from 'react';
import styled from '@emotion/styled';
import {
  palette,
  radius,
  spacing,
  typography,
} from '../../../../../style-utils/theme';
import { formatSpan } from './chart-theme';

const DeltaChip = styled.span<{ unlike?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  padding: 0 ${spacing.half};
  border-radius: ${radius.xs};
  background: ${({ unlike }) => (unlike ? 'transparent' : palette.surfaceMuted)};
  border: ${({ unlike }) => (unlike ? `1px dashed ${palette.line}` : 'none')};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  font-variant-numeric: tabular-nums;
  color: ${palette.slate};
  white-space: nowrap;
`;

const DeltaGlyph = styled.span`
  font-size: ${typography.size.xxs}px;
  line-height: 1;
`;

const SpanNote = styled.span`
  display: inline-block;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  line-height: ${typography.leading.snug};
`;

export interface DeltaProps {
  delta: number | null;
  compareValue: number | null;
  /** False means the two windows are different lengths. */
  likeForLike: boolean;
  curSpanMs?: number;
  prevSpanMs?: number;
  /** `live` figures carry no delta AT ALL, by design. */
  kind?: 'window' | 'as_of' | 'live';
}

export const Delta: React.FC<DeltaProps> = ({
  delta,
  compareValue,
  likeForLike,
  curSpanMs,
  prevSpanMs,
  kind,
}) => {
  if (kind === 'live') {
    // Just "no comparison". The tile beside this already carries the "as of now"
    // tag, and the two together read as "as of now as of now — no comparison",
    // which is what the first browser pass on the analytics screen showed.
    return <SpanNote data-testid="delta-live-note">no comparison</SpanNote>;
  }
  if (delta == null) return null;

  const glyph = delta > 0 ? '▲' : delta < 0 ? '▼' : '■';
  const signed = `${delta > 0 ? '+' : ''}${delta.toLocaleString()}`;
  const share =
    likeForLike && compareValue != null && compareValue !== 0
      ? `${delta > 0 ? '+' : ''}${Math.round((delta / compareValue) * 100)}%`
      : null;

  return (
    <>
      <DeltaChip unlike={!likeForLike} data-testid="delta-chip">
        <DeltaGlyph aria-hidden="true">{glyph}</DeltaGlyph>
        {signed}
        {share ? ` (${share})` : ''}
      </DeltaChip>
      {!likeForLike && curSpanMs != null && prevSpanMs != null ? (
        <SpanNote data-testid="unlike-spans">
          {formatSpan(curSpanMs)} vs {formatSpan(prevSpanMs)} — different lengths,
          so no percentage
        </SpanNote>
      ) : null}
    </>
  );
};
